"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { Cookie } from "lucide-react";
import { AeraButton } from "@/app/components/ui/AeraButton";
import CookieConsent from "react-cookie-consent";
import {
  AERA_COOKIE_CONSENT_NAME,
  AERA_COOKIE_CONSENT_OPEN_EVENT,
} from "@/lib/cookieConsent";
import { OAUTH_APP_NAME } from "@/lib/siteLegal";
import { BILLLIN_GO_PATH } from "@/lib/billlinGoPath";

function normalizePathname(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
}

function isSigninPath(pathname: string | null): boolean {
  if (!pathname) return false;
  const normalized = normalizePathname(pathname);
  return normalized === "/signin" || normalized.startsWith("/signin/");
}

/** LIFF deep-link router — transient loading, no cookie modal. */
function isBilllinGoPath(pathname: string | null): boolean {
  if (!pathname) return false;
  const normalized = normalizePathname(pathname);
  return normalized === BILLLIN_GO_PATH;
}

/** OAuth redirect / transient handlers — no cookie modal. */
function isAuthCallbackPath(pathname: string | null): boolean {
  if (!pathname) return false;
  const normalized = normalizePathname(pathname);
  return (
    normalized === "/oauth/close" ||
    /^\/auth\/[^/]+\/callback$/.test(normalized)
  );
}

export default function CookieConsentBanner() {
  const pathname = usePathname();
  const [bannerKey, setBannerKey] = useState(0);

  useEffect(() => {
    const handleOpen = () => setBannerKey((k) => k + 1);
    window.addEventListener(AERA_COOKIE_CONSENT_OPEN_EVENT, handleOpen);
    return () =>
      window.removeEventListener(AERA_COOKIE_CONSENT_OPEN_EVENT, handleOpen);
  }, []);

  if (
    isSigninPath(pathname) ||
    isBilllinGoPath(pathname) ||
    isAuthCallbackPath(pathname)
  ) {
    return null;
  }

  return (
    <CookieConsent
      key={bannerKey}
      location="bottom"
      cookieName={AERA_COOKIE_CONSENT_NAME}
      cookieValue="accepted"
      declineCookieValue="declined"
      setDeclineCookie
      expires={365}
      enableDeclineButton
      hideOnAccept
      hideOnDecline
      disableStyles
      disableButtonStyles
      buttonText="ยอมรับ"
      declineButtonText="ไม่ยอมรับ"
      ariaAcceptLabel="ยอมรับการใช้คุกกี้"
      ariaDeclineLabel="ไม่ยอมรับคุกกี้ที่ไม่จำเป็น"
      style={{
        position: "fixed",
        zIndex: 50,
        display: "flex",
        bottom: "max(2rem, calc(1.25rem + env(safe-area-inset-bottom)))",
        left: "50%",
        transform: "translateX(-50%)",
        width: "min(56rem, calc(100% - 2rem))",
        alignItems: "stretch",
        pointerEvents: "auto",
      }}
      containerClasses="!flex !flex-col !gap-5 !rounded-t-2xl !border !border-aera-lightgray/80 !border-t-[3px] !border-t-aera-amber !bg-aera-white !p-4 !shadow-none !backdrop-blur-md font-[family-name:var(--font-ibm-plex-thai)] !pointer-events-auto"
      contentClasses="!m-0 !min-w-0 !p-0"
      buttonWrapperClasses="!m-0 !flex !w-full !shrink-0 !flex-row !items-center !justify-end !gap-3 !border-t !border-aera-lightgray/60 !pt-4"
      declineButtonClasses="!m-0 !min-h-9 !flex-1 sm:!flex-none !cursor-pointer !rounded-xl !border !border-aera-lightgray !bg-aera-white !px-4 !py-2 !text-sm !font-medium !text-aera-charcoal !transition-colors hover:!border-aera-amber hover:!bg-aera-offwhite"
      buttonClasses="!m-0 !min-h-9 !flex-1 sm:!flex-none !cursor-pointer !rounded-xl !border !border-aera-amber !bg-aera-amber !px-5 !py-2 !text-sm !font-semibold !text-aera-charcoal !transition-colors hover:!border-aera-charcoal hover:!bg-aera-charcoal hover:!text-aera-white"
    >
      <div className="flex gap-4">
        <div
          className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-aera-warmcream text-aera-amberdark"
          aria-hidden
        >
          <Cookie className="h-5 w-5" strokeWidth={2} />
        </div>
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-semibold leading-snug text-aera-charcoal">
            การตั้งค่าความเป็นส่วนตัวบน {OAUTH_APP_NAME}
          </p>
          <p className="text-xs leading-relaxed text-aera-slate sm:text-sm">
            เราใช้คุกกี้ที่จำเป็นสำหรับการเข้าสู่ระบบและความปลอดภัยของบัญชี
            พร้อมข้อมูลการใช้งานแบบไม่ระบุตัวตนเพื่อปรับปรุงแพลตฟอร์ม
            คุณเลือกยอมรับหรือไม่ยอมรับได้ทุกเมื่อ — รายละเอียดอยู่ใน{" "}
            <AeraButton
              href="/privacy"
              variant="link"
              size="xs"
              className="inline text-aera-amber hover:text-aera-charcoal"
            >
              นโยบายความเป็นส่วนตัว
            </AeraButton>
          </p>
        </div>
      </div>
    </CookieConsent>
  );
}
